/**
 * DynFlipHandle — toggles a two-state dynamic block property (AutoCAD flip state).
 *
 * Behaves as a button, not a drag handle: the InteractionManager turns a tap on an
 * overlay into start() immediately followed by end(), the same way DeleteLeaderHandle
 * works. A real drag is treated as a cancelled click.
 */

import type { DRDrawingViewer } from "../../DRDrawingViewer";
import { trackShapeChange } from "@/analytics/drawing-analytics";
import type { CommandHistory } from "../../commands/CommandHistory";
import type { TransformContext, Point3D } from "../types";
import { HandleType, HandlePosition, HANDLE_SIZES } from "../types";
import { AbstractHandle } from "./AbstractHandle";
import { setDynBlockProperty } from "../../blocks/dynamic";
import { SetDynBlockPropertyCommand } from "../../commands/SetDynBlockPropertyCommand";
import { findEntityByHandle } from "../../core/oda";
import { deleteObj } from "../../core/delete";
import { selectionService } from "../../selection";
import { Logger } from "../../../logger";

/** Blue-ish fill to distinguish dynamic block handles from transform ones. */
const DYN_FLIP_DRAW_OPTIONS = {
  fillColor: [70, 130, 220] as [number, number, number],
  strokeColor: [255, 255, 255] as [number, number, number],
  // Double-headed arrow: the flip grip swaps between two states.
  shape: "doubleArrow" as const,
  radius: HANDLE_SIZES.default * HANDLE_SIZES.handleScale,
};

/** Movement (in screen pixels) above which the gesture is a drag, not a click. */
const CLICK_THRESHOLD_PX = 5;

export class DynFlipHandle extends AbstractHandle {
  readonly type = HandleType.DynFlip;
  readonly position = HandlePosition.DynProperty;

  /** Flip handles are buttons: nothing to snap to. */
  snapOptions = { acceptOrtho: false, acceptPolar: false, acceptObject: false };

  private startPosition: Point3D | null = null;

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    private readonly entityHandle: string,
    private readonly propertyName: string,
    /** The two allowed values, in definition order. */
    private readonly allowedValues: string[],
    /** Value at the time the handle was created. */
    private readonly currentValue: string,
    worldPosition: Point3D,
  ) {
    super(viewer, history);
    this.drawOptions = { ...DYN_FLIP_DRAW_OPTIONS };
    this.worldPosition = { ...worldPosition };
  }

  start(context: TransformContext): void {
    if (this.isTransforming) return;
    this.isTransforming = true;
    this.startPosition = { ...context.worldPoint };
  }

  ongoing(): void {
    // Nothing to preview: the flip is instantaneous on release.
  }

  end(context: TransformContext): void {
    if (!this.isTransforming) return;
    this.isTransforming = false;

    const wasClick = this.isClick(context);
    this.startPosition = null;
    if (!wasClick) return;

    const nextValue =
      this.allowedValues.find((value) => value !== this.currentValue) ??
      this.allowedValues[0];
    if (nextValue === undefined) return;

    const entityId = findEntityByHandle(
      this.module,
      this.viewer.database,
      this.entityHandle,
    );
    if (!entityId || entityId.isNull()) return;

    const change = setDynBlockProperty(
      this.module,
      entityId,
      this.propertyName,
      nextValue,
    );
    deleteObj(entityId);
    if (!change) return;

    Logger.info(`DynFlipHandle : ${this.propertyName} -> ${nextValue}`);
    trackShapeChange("dyn_block_param_changed", {
      property: this.propertyName,
    });
    this.history.execute(
      new SetDynBlockPropertyCommand(this.viewer, {
        blockHandle: this.entityHandle,
        changes: [change],
      }),
    );

    // Flipping re-evaluates the block into a new anonymous representation.
    this.viewer.forceRegen();
    selectionService.updateGrid();
  }

  cancel(): void {
    if (!this.isTransforming) return;
    this.isTransforming = false;
    this.startPosition = null;
  }

  /** True when the pointer barely moved, i.e. the gesture was a tap. */
  private isClick(context: TransformContext): boolean {
    if (!this.startPosition) return true;
    const dx = context.worldPoint.x - this.startPosition.x;
    const dy = context.worldPoint.y - this.startPosition.y;
    const moved = Math.sqrt(dx * dx + dy * dy);
    const threshold = Math.abs(
      this.viewer.screenDistanceToWorld(CLICK_THRESHOLD_PX),
    );
    return moved <= threshold;
  }
}
